import { createAsyncThunk } from '@reduxjs/toolkit';
import type { LayerRegistryEntry } from '@okmaps/shared';
import type { AppDispatch, RootState } from '../../app/store.js';
import { layerApi } from '../../services/layerApi.js';
import { extentRequested } from '../slices/mapSlice.js';
import { layerAdded } from '../slices/layersSlice.js';
import { toPendingExtent } from './toPendingExtent.js';

/**
 * Add a catalog record to the map.
 *
 * This is the one operation that genuinely needs a thunk rather than a plain
 * action or an RTK Query hook: it has to resolve a catalog id against the layer
 * registry, decide whether the layer is renderable at all, commit it to the
 * layer stack, and then optionally frame its extent - four steps across two
 * slices and one cached query.
 *
 * The registry lookup goes through RTK Query's cache rather than a fresh fetch,
 * so after the first call this is synchronous in practice.
 */
export const addLayerToMap = createAsyncThunk<
  LayerRegistryEntry,
  { layerName: string; zoomTo?: boolean },
  { state: RootState; dispatch: AppDispatch; rejectValue: string }
>('layers/addLayerToMap', async ({ layerName, zoomTo = false }, thunk) => {
  const registry = await thunk
    .dispatch(layerApi.endpoints.getLayerRegistry.initiate())
    .unwrap();

  const entry = registry.find((l) => l.name === layerName);
  if (!entry) {
    return thunk.rejectWithValue(
      `"${layerName}" is in the catalog but is not published as a drawable layer.`,
    );
  }

  thunk.dispatch(
    layerAdded({
      name: entry.name,
      title: entry.title,
      kind: entry.kind,
      queryable: entry.queryable,
      // Carried onto the map layer so the SDK never asks for a tile the server
      // would answer with an empty image, and so the layer list can say why a
      // layer it is showing is not on screen.
      ...(entry.minScale === undefined ? {} : { minScale: entry.minScale }),
      ...(entry.maxScale === undefined ? {} : { maxScale: entry.maxScale }),
    }),
  );

  const target = zoomTo ? toPendingExtent(entry) : null;
  if (target) thunk.dispatch(extentRequested(target));

  return entry;
});
